import type { MessageView } from '@broker/contracts';
import { Link, useParams } from 'react-router';
import { useCorrelation, useCorrelations } from '../api/queries.js';
import { Badge, Card, Loading, Mono, Page, Problem, Table, toneForStatus } from '../components/ui.jsx';
import { formatDateTime, formatDuration, formatNumber, formatRelative } from '../utils/format.js';

/**
 * A correlation is one originating fact and everything it caused. It is the answer to the only
 * question that matters when something went wrong across five applications: what happened, in
 * what order, and where did it stop.
 */
export function Correlations() {
  const correlations = useCorrelations();

  if (correlations.isLoading) return <Loading what="le correlazioni" />;
  if (correlations.error) return <Problem error={correlations.error} />;

  return (
    <Page eyebrow="OSSERVA" title="Correlations" description="Ogni catena di messaggi nata da un singolo fatto.">
      <Card>
        <Table
          head={
            <tr>
              <th>Correlazione</th>
              <th>Iniziata</th>
              <th>Conclusa</th>
              <th className="right">Messaggi</th>
            </tr>
          }
          empty="Nessuna correlazione ancora."
        >
          {correlations.data!.correlations.map((correlation) => (
            <tr key={correlation.correlationId}>
              <td>
                <Link to={`/correlations/${encodeURIComponent(correlation.correlationId)}`}>
                  <Mono value={correlation.correlationId} />
                </Link>
              </td>
              <td className="muted small">{formatDateTime(correlation.startedAt)}</td>
              <td className="muted small">{correlation.endedAt ? formatRelative(correlation.endedAt) : 'in corso'}</td>
              <td className="right">{formatNumber(correlation.messageCount)}</td>
            </tr>
          ))}
        </Table>
      </Card>
    </Page>
  );
}

export function CorrelationDetail() {
  const { correlationId = '' } = useParams();
  const correlation = useCorrelation(correlationId);

  if (correlation.isLoading) return <Loading what="la correlazione" />;
  if (correlation.error) return <Problem error={correlation.error} />;
  const data = correlation.data!;

  const byParent = new Map<string | null, MessageView[]>();
  for (const message of data.messages) {
    const parent = data.edges.find((edge) => edge.child === message.id)?.parent ?? null;
    // A message whose cause is outside this correlation is a root of the tree as far as we can see.
    const key = data.messages.some((candidate) => candidate.id === parent) ? parent : null;
    byParent.set(key, [...(byParent.get(key) ?? []), message]);
  }

  return (
    <Page
      eyebrow="OSSERVA"
      title="Correlazione"
      description={`${data.messages.length} messaggi, da ${formatDateTime(data.startedAt)}.`}
      actions={<Mono value={data.correlationId} />}
    >
      <Card title="Catena causale" hint="Ogni livello è un messaggio causato da quello sopra">
        {data.messages.length === 0 ? <p className="muted">Nessun messaggio.</p> : <Branch parent={null} byParent={byParent} depth={0} />}
      </Card>
    </Page>
  );
}

function Branch({ parent, byParent, depth }: { parent: string | null; byParent: Map<string | null, MessageView[]>; depth: number }) {
  const children = byParent.get(parent) ?? [];
  if (children.length === 0) return null;
  return (
    <ul className="tree" style={{ marginLeft: depth === 0 ? 0 : 20 }}>
      {children.map((message) => (
        <li key={message.id}>
          <div className="tree-node">
            <Link to={`/messages/${message.id}`}>
              <strong>{message.type}</strong>
            </Link>
            <span className="muted small">
              {message.producer} → {message.channel}
            </span>
            <Badge tone={toneForStatus(message.status)}>{message.status}</Badge>
            <span className="muted small">{formatDuration(message.processingMs)}</span>
          </div>
          <Branch parent={message.id} byParent={byParent} depth={depth + 1} />
        </li>
      ))}
    </ul>
  );
}
